<template>
    <div id="employeeperformancepage">
        <actions-menu>
            <actions-menu-item
                :v4="true"
                :action="() => $router.push('/employeedetails/' + idParam)"
                icon="arrow-left"
                :label="trans('pages.employee_performance.to_details')"
            />
        </actions-menu>

        <spinner v-if="loading" />
        <div id="emloyeePerformanceChart" />

        <div id="chartControls">
            <ui5-button
                id="previousDate"
                @click="shiftDateForPerformanceGraph('previous')"
            >
                « {{ trans('pages.employee_performance.previous') }}
            </ui5-button>
            {{ trans('pages.employee_performance.performance_for') }}
            <ui5-select
                @change="
                    dateRange = $event.detail.selectedOption.value;
                    load();
                "
            >
                <ui5-option
                    value="week"
                    :selected="dateRange == 'week'"
                >
                    {{ trans('pages.employee_performance.week') }}
                </ui5-option>
                <ui5-option
                    value="month"
                    :selected="dateRange == 'month'"
                >
                    {{ trans('pages.employee_performance.month') }}
                </ui5-option>
            </ui5-select>
            {{ trans('pages.employee_performance.of') }}
            <span class="dateForPerformanceGraph">
                {{ dateForPerformanceGraph }}
            </span>

            <ui5-button
                id="nextDate"
                @click="shiftDateForPerformanceGraph('next')"
            >
                {{ trans('pages.employee_performance.next') }} »
            </ui5-button>
        </div>
    </div>
</template>
<script>
import IdParamMixin from '../mixins/id-param';
import ActionsMenu from '../components/ActionsMenu';
import ActionsMenuItem from '../components/ActionsMenuItem';
import Spinner from '../components/loader/Spinner';
import userApi from '../api/user';
import productApi from '../api/product';
import { parseDate } from '../helpers/DateHelper';
import { showErrorMessage } from '../dialogs';
import localization from '../mixins/localization';

export default {
    components: { Spinner, ActionsMenu, ActionsMenuItem },
    mixins: [IdParamMixin('idParam', 'dateForPerformanceGraph'), localization],
    mounted() {
        this.load();
    },
    computed: {
        dateForPerformanceGraph() {
            if (this.$route.query.date) {
                return this.$route.query.date;
            }
            const currentDate = new Date();
            return (
                currentDate.getFullYear() +
                '-' +
                (currentDate.getMonth() + 1) +
                '-' +
                currentDate.getDate()
            );
        },
    },
    data() {
        return {
            loading: false,
            dateRange: 'week',
            employee: null,
        };
    },
    methods: {
        load() {
            this.loading = true;

            Promise.all([
                userApi.fetchUserDetails(this.idParam).then((response) => {
                    const employee = response.data.data;
                    this.$store.commit(
                        'general/setPageTitle',
                        this.trans(
                            'pages.employee_performance.performance_for_employee'
                        ) +
                            ' ' +
                            employee.name,
                        { root: true }
                    );
                    this.employee = employee;
                }),
                productApi
                    .getTimeSpent(
                        this.idParam,
                        this.dateForPerformanceGraph,
                        this.dateRange
                    )
                    .then((response) => {
                        const dataPrepared =
                            this.prepareEmployeePerformanceDataForGraph(
                                response.data.data
                            );
                        this.drawEmployeePerformanceGraph(
                            dataPrepared.xAxisLabels,
                            dataPrepared.graphSeries
                        );
                    }),
            ]).finally(() => {
                this.loading = false;
            });
        },
        prepareEmployeePerformanceDataForGraph(data) {
            const xAxisLabels = [];

            const series = {};
            const allDates = {};

            // Gather all dates
            Object.entries(data).forEach(([key, val]) => {
                if (!allDates.hasOwnProperty(val.date)) {
                    xAxisLabels.push(val.date);
                    allDates[val.date] = null;
                }
            });

            // Gather all status info for the dates
            Object.entries(data).forEach(([key, val]) => {
                // Create new status object if it doesn't exist yet
                if (!series[val.status]) {
                    var allDatesPerStatus = allDates;
                    series[val.status] = {
                        status: val.status,
                        dates: {},
                    };
                    // Provide all date attributes for each status object
                    Object.entries(allDates).forEach(([dateKey, dateVal]) => {
                        series[val.status].dates[dateKey] = null;
                    });
                }

                // Add the amount for the specific date of a specific status
                series[val.status].dates[val.date] += parseFloat(val.amount);
            });

            // Convert info objects to format graph can use
            const graphSeries = Object.entries(series).map(([key, val]) => {
                return {
                    name: val.status,
                    data: Object.entries(val.dates).map(
                        ([dateKey, dateVal]) => {
                            return dateVal;
                        }
                    ),
                };
            });

            return {
                xAxisLabels,
                graphSeries,
            };
        },
        drawEmployeePerformanceGraph(xAxisLabels, series) {
            const $chart = $('#emloyeePerformanceChart');

            if (series.length == 0) {
                $chart.html(
                    `<div class="noResultsForEmployeePerformance">${this.trans(
                        'pages.employee_performance.no_results'
                    )}</div>`
                );
                return;
            }

            $chart.highcharts({
                chart: {
                    type: 'column',
                },
                title: {
                    text: this.trans('pages.employee_performance.performance'),
                },
                xAxis: {
                    categories: xAxisLabels,
                },
                yAxis: {
                    min: 0,
                    title: {
                        text: this.trans(
                            'pages.employee_performance.total_hours_worked'
                        ),
                    },
                    stackLabels: {
                        enabled: true,
                        style: {
                            fontWeight: 'bold',
                            color:
                                (Highcharts.theme &&
                                    Highcharts.theme.textColor) ||
                                'gray',
                        },
                    },
                },
                legend: {
                    align: 'right',
                    x: -30,
                    verticalAlign: 'top',
                    y: 25,
                    floating: true,
                    backgroundColor:
                        (Highcharts.theme && Highcharts.theme.background2) ||
                        'white',
                    borderColor: '#CCC',
                    borderWidth: 1,
                    shadow: false,
                },
                tooltip: {
                    formatter() {
                        return (
                            '<b>' +
                            this.x +
                            '</b><br/>' +
                            this.series.name +
                            ': ' +
                            this.y +
                            '<br/>' +
                            'Total: ' +
                            this.point.stackTotal
                        );
                    },
                },
                plotOptions: {
                    column: {
                        stacking: 'normal',
                        dataLabels: {
                            enabled: true,
                            color:
                                (Highcharts.theme &&
                                    Highcharts.theme.dataLabelsColor) ||
                                'white',
                            style: {
                                textShadow: '0 0 3px black',
                            },
                            formatter() {
                                if (this.point.y != null) {
                                    return this.series.name;
                                }
                            },
                        },
                    },
                },
                series: series,
                credits: {
                    enabled: false,
                },
            });
        },
        shiftDateForPerformanceGraph(direction) {
            // Determine amount and direction of shift (forward, backward, month or week)
            let amount;
            if (this.dateRange == 'week') {
                amount = 604800;
            } else if (this.dateRange == 'month') {
                amount = 2592200; //maand
            } else {
                return showErrorMessage(
                    this.trans('pages.employee_performance.error')
                );
            }
            if (direction == 'previous') {
                amount *= -1;
            }

            const date = parseDate(this.dateForPerformanceGraph);
            const newTime = date.getTime() + amount * 1000;
            const newDate = new Date(newTime);
            this.$router.push(
                '/employeeperformance/' +
                    this.idParam +
                    '?date=' +
                    newDate.getFullYear() +
                    '-' +
                    (newDate.getMonth() + 1) +
                    '-' +
                    newDate.getDate()
            );
        },
    },
};
</script>
